import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../store/auth';
import { apiFetch } from '../../lib/api-client';
import styles from './PlaceAutocomplete.module.css';

function utcOffsetLabel(tz: string, locale: string): string | null {
  try {
    const parts = new Intl.DateTimeFormat(locale, {
      timeZone: tz,
      timeZoneName: 'shortOffset',
    }).formatToParts(new Date());
    const offset = parts.find((p) => p.type === 'timeZoneName')?.value;
    if (!offset) return null;
    // "GMT+3" → "UTC+03:00"; "GMT+05:30" → "UTC+05:30"; "GMT" → "UTC+00:00"
    const m = /^GMT([+-])(\d{1,2})(?::?(\d{2}))?$/.exec(offset);
    if (!m) {
      if (offset === 'GMT') return 'UTC+00:00';
      return offset.replace('GMT', 'UTC');
    }
    const sign = m[1];
    const hh = m[2].padStart(2, '0');
    const mm = (m[3] ?? '00').padStart(2, '0');
    return `UTC${sign}${hh}:${mm}`;
  } catch {
    return null;
  }
}

export interface SelectedPlace {
  placeId: string;
  placeName: string;
  state: string | null;
  country: string | null;
  countryCode: string | null;
  lat: number;
  lng: number;
  tz: string;
}

interface Candidate {
  placeId: string;
  placeName: string;
  /** Short name for the first line; older servers sent only placeName. */
  name?: string;
  /** District («Воскресенский округ») — tells namesake villages apart. */
  county?: string | null;
  state: string | null;
  country: string | null;
  countryCode: string | null;
  lat: number;
  lng: number;
  tz: string | null;
}

interface Props {
  value: SelectedPlace | null;
  onChange: (place: SelectedPlace | null) => void;
  placeholder?: string;
  ariaLabel?: string;
  /** Defaults to the app language. */
  lang?: 'ru' | 'uk' | 'en';
}

const DEBOUNCE_MS = 400;
const MIN_QUERY_LEN = 2;

function formatSecondary(c: Candidate): string {
  return [c.county, c.state, c.country].filter(Boolean).join(', ');
}

function primaryName(c: Candidate): string {
  if (c.name) return c.name;
  // placeName is "Name, State, Country" — primary is the leading segment.
  const head = c.placeName.split(',')[0]?.trim();
  return head || c.placeName;
}

export function PlaceAutocomplete({ value, onChange, placeholder, ariaLabel, lang: langProp }: Props): JSX.Element {
  const jwt = useAuth((s) => s.jwt);
  const { i18n, t } = useTranslation('flows');
  const lang = langProp ?? (i18n.language.startsWith('uk') ? 'uk' : i18n.language.startsWith('en') ? 'en' : 'ru');
  const utcLabel = useMemo(
    () => (value?.tz ? utcOffsetLabel(value.tz, i18n.language) : null),
    [value?.tz, i18n.language],
  );
  const [text, setText] = useState(value?.placeName ?? '');
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [activeIdx, setActiveIdx] = useState(-1);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const lastIssuedQuery = useRef<string>('');

  // Sync text when external value changes (e.g. parent resets form)
  useEffect(() => {
    setText(value?.placeName ?? '');
  }, [value]);

  // Debounced search
  useEffect(() => {
    const query = text.trim();
    if (value && query === value.placeName) return; // selection already matches input
    setNotFound(false);
    if (query.length < MIN_QUERY_LEN) {
      setCandidates([]);
      setOpen(false);
      setLoading(false);
      return;
    }
    const t = setTimeout(() => {
      void runSearch(query);
    }, DEBOUNCE_MS);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  async function runSearch(query: string): Promise<void> {
    if (!jwt) return;
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    lastIssuedQuery.current = query;
    setLoading(true);
    try {
      const res = await apiFetch(
        `/api/v1/places/search?q=${encodeURIComponent(query)}&lang=${lang}`,
        jwt,
        { signal: ctrl.signal },
      );
      if (!res.ok) throw new Error(`search_${res.status}`);
      const data = (await res.json()) as { candidates: Candidate[] };
      // Ignore stale responses
      if (lastIssuedQuery.current !== query) return;
      setCandidates(data.candidates);
      setNotFound(data.candidates.length === 0);
      setOpen(data.candidates.length > 0);
      setActiveIdx(data.candidates.length > 0 ? 0 : -1);
    } catch (err) {
      if ((err as { name?: string }).name === 'AbortError') return;
      setCandidates([]);
      setOpen(false);
    } finally {
      if (lastIssuedQuery.current === query) setLoading(false);
    }
  }

  function pick(c: Candidate): void {
    if (!c.tz) return; // backend filters these out, defensive
    const selected: SelectedPlace = {
      placeId: c.placeId,
      placeName: c.placeName,
      state: c.state,
      country: c.country,
      countryCode: c.countryCode,
      lat: c.lat,
      lng: c.lng,
      tz: c.tz,
    };
    setText(selected.placeName);
    setOpen(false);
    setCandidates([]);
    onChange(selected);
  }

  function handleInput(e: React.ChangeEvent<HTMLInputElement>): void {
    const next = e.target.value;
    setText(next);
    if (value && next !== value.placeName) onChange(null); // invalidate selection on edit
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>): void {
    if (!open || candidates.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => (i + 1) % candidates.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => (i <= 0 ? candidates.length - 1 : i - 1));
    } else if (e.key === 'Enter') {
      if (activeIdx >= 0) {
        e.preventDefault();
        pick(candidates[activeIdx]);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  }

  return (
    <div className={styles.root}>
      <div className={styles.inputWrap}>
        <span
          className={styles.pin}
          aria-hidden="true"
          aria-label={t('onboarding.birthData.placePinAria', { defaultValue: 'Place' })}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 22s-7-6.2-7-12a7 7 0 0 1 14 0c0 5.8-7 12-7 12z" />
            <circle cx="12" cy="10" r="2.5" />
          </svg>
        </span>
        <input
          type="text"
          className={`${styles.input} ${styles.inputWithPin}`}
          value={text}
          onChange={handleInput}
          onKeyDown={handleKeyDown}
          onFocus={() => candidates.length > 0 && setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 120)}
          placeholder={placeholder}
          aria-label={ariaLabel}
          aria-autocomplete="list"
          aria-expanded={open}
          aria-controls="place-listbox"
          aria-activedescendant={activeIdx >= 0 ? `place-opt-${activeIdx}` : undefined}
          role="combobox"
          autoComplete="off"
          spellCheck={false}
        />
        {utcLabel && (
          <span className={styles.utc} aria-label={`Timezone ${utcLabel}`}>{utcLabel}</span>
        )}
      </div>
      {loading && <span className={styles.spinner} aria-hidden="true" />}
      {!loading && notFound && !value && (
        <p className={styles.notFound} role="status">{t('onboarding.birthData.placeNotFound')}</p>
      )}
      {open && candidates.length > 0 && (
        <ul id="place-listbox" role="listbox" className={styles.list}>
          {candidates.map((c, i) => (
            <li
              key={c.placeId}
              id={`place-opt-${i}`}
              role="option"
              aria-selected={i === activeIdx}
              className={`${styles.item} ${i === activeIdx ? styles.itemActive : ''}`}
              onMouseDown={(e) => { e.preventDefault(); pick(c); }}
              onMouseEnter={() => setActiveIdx(i)}
            >
              <span className={styles.itemPrimary}>{primaryName(c)}</span>
              <span className={styles.itemSecondary}>{formatSecondary(c)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
